import { Paths } from "@app/routes";
import { ConfirmAction, FormRenderer, WithPermission } from "@atlas/composite";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  type CellContext,
  type ColumnDef,
  DataGrid,
  DataGridColumnHeader,
  Label,
  type PaginationState,
  ToolbarGroup,
  useToast,
} from "@atlas/core";
import {
  FilterBuilder,
  useFormBuilder,
  usePermission,
  useTranslation,
} from "@atlas/sdk";
import type { IHubCatalogType } from "@models";
import {
  HUB_CATALOG_TYPES_DEFAULT_SORT,
  useDeleteHubCatalogType,
  useSearchHubCatalogTypes,
} from "@query";
import { DetailsPanel, HUB_PERM } from "@shared";
import type { AxiosError } from "axios";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router";

const DATA_GRID_LAYOUT = {
  columnsVisibility: true,
  headerBackground: true,
  tableBorder: true,
  height: "full",
  headerSticky: true,
} as const;

export function CatalogTypeManagerPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { isPermission: hasCatalogTypeManage } = usePermission([
    HUB_PERM.CATALOG_TYPE_MANAGE,
  ]);

  const [searchQuery, setSearchQuery] = useState("");
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const filter = useMemo(() => {
    const builder = new FilterBuilder();
    if (searchQuery.trim()) {
      builder.contains("label", searchQuery.trim());
    }
    return builder.build();
  }, [searchQuery]);

  const { data: catalogTypesData, isLoading } = useSearchHubCatalogTypes({
    filter,
    mode: "DATA",
    sort: [...HUB_CATALOG_TYPES_DEFAULT_SORT],
    startIndex: pagination.pageIndex * pagination.pageSize,
    limit: pagination.pageSize,
  });
  const catalogTypes = useMemo(
    () =>
      catalogTypesData?.pages.flatMap(
        (page: { items: IHubCatalogType[] }) => page.items,
      ) ?? [],
    [catalogTypesData],
  );

  const totalCount = useMemo(
    () => catalogTypesData?.pages[0]?.totalCount ?? 0,
    [catalogTypesData],
  );

  const { mutate: deleteCatalogType } = useDeleteHubCatalogType();
  const [deleteTarget, setDeleteTarget] = useState<IHubCatalogType | null>(
    null,
  );
  const [selectedCatalogType, setSelectedCatalogType] =
    useState<IHubCatalogType | null>(null);

  const { data: formData, isLoading: isFormLoading } = useFormBuilder(
    selectedCatalogType?.formId ?? 0,
    {
      enabled: !!selectedCatalogType?.formId && selectedCatalogType.formId > 0,
    },
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteCatalogType(deleteTarget, {
      onSuccess: () => {
        setDeleteTarget(null);
        toast.add({
          title: t("hub.catalog_type_manager.delete_success"),
          description: t("hub.catalog_type_manager.delete_success_description"),
          data: { type: "success" },
        });
      },
      onError: (error) => {
        const axiosError = error as AxiosError<{ message?: string }>;
        const apiMessage =
          axiosError?.response?.data?.message || axiosError?.message;

        const errorMessage =
          apiMessage || t("hub.catalog_type_manager.delete_error_description");

        toast.add({
          title: t("hub.catalog_type_manager.delete_error"),
          description: errorMessage,
          data: { type: "error" },
        });
      },
    });
  }, [deleteTarget, deleteCatalogType, toast, t]);

  const handleRowClick = useCallback((row: IHubCatalogType) => {
    setSelectedCatalogType(row);
  }, []);

  const handleClosePanel = useCallback(() => {
    setSelectedCatalogType(null);
  }, []);

  const idOrNameToLabelMap = useMemo(() => {
    const map = new Map<string, string>();
    catalogTypes.forEach((ct: IHubCatalogType) => {
      const label = ct.label || ct.name;
      map.set(String(ct.typeId ?? ""), label);
      map.set(ct.name, label);
    });
    return map;
  }, [catalogTypes]);

  const parentCatalogTypeLabels = useMemo(() => {
    if (
      !selectedCatalogType?.parentCatalogTypes?.length ||
      !catalogTypes.length
    )
      return undefined;
    return selectedCatalogType.parentCatalogTypes
      .map(
        (idOrName) =>
          idOrNameToLabelMap.get(String(idOrName)) ?? String(idOrName),
      )
      .filter(Boolean);
  }, [
    selectedCatalogType?.parentCatalogTypes,
    catalogTypes,
    idOrNameToLabelMap,
  ]);

  const detailsPanelItems = useMemo(
    () =>
      selectedCatalogType
        ? [
            {
              label: t("hub.catalog_type_manager.label"),
              value: selectedCatalogType.label,
            },
            {
              label: t("hub.catalog_subscribe.solution_description"),
              value: selectedCatalogType.description,
            },
            {
              label: t("hub.catalog_type_manager.parent_catalog_types"),
              value: parentCatalogTypeLabels?.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {parentCatalogTypeLabels.map((label) => (
                    <Badge
                      key={label}
                      variant="secondary"
                      appearance="default"
                      size="sm">
                      {label}
                    </Badge>
                  ))}
                </div>
              ) : undefined,
            },
          ]
        : [],
    [selectedCatalogType, parentCatalogTypeLabels, t],
  );

  const handleEditCatalogType = useCallback(() => {
    if (selectedCatalogType?.typeId != null) {
      navigate(
        `/${Paths.CatalogTypeManager}/${Paths.EditCatalogType}?typeId=${selectedCatalogType.typeId}`,
      );
    }
  }, [selectedCatalogType?.typeId, navigate]);

  const detailsPanelOnEdit = useMemo(
    () =>
      selectedCatalogType?.typeId != null ? handleEditCatalogType : undefined,
    [selectedCatalogType?.typeId, handleEditCatalogType],
  );

  const afterActions = useMemo(
    () =>
      hasCatalogTypeManage ? (
        <Button asChild variant="outline">
          <Link to={`/${Paths.CatalogTypeManager}/${Paths.CreateCatalogType}`}>
            <Plus className="mr-2 size-4" />
            {t("hub.catalog_manager.breadcrumb_contribute")}
          </Link>
        </Button>
      ) : null,
    [hasCatalogTypeManage, t],
  );

  const headerTextClassName =
    "text-foreground px-1 py-0.5 font-semibold text-sm";

  const columns = useMemo<ColumnDef<IHubCatalogType, unknown>[]>(
    () => [
      {
        accessorKey: "label",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.catalog_type_manager.label")}
            className={headerTextClassName}
          />
        ),
        cell: (info: CellContext<IHubCatalogType, unknown>) =>
          (info.getValue() as string) ?? "",
      },
      {
        accessorKey: "description",
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.catalog_subscribe.solution_description")}
            className={headerTextClassName}
          />
        ),
        cell: (info: CellContext<IHubCatalogType, unknown>) => (
          <Label className="block min-w-0 truncate">
            {(info.getValue() as string) ?? ""}
          </Label>
        ),
      },
      {
        id: "parentCatalogTypes",
        accessorFn: (row) => row.parentCatalogTypes,
        header: (info) => (
          <DataGridColumnHeader
            column={info.column}
            title={t("hub.catalog_type_manager.parent_catalog_types")}
            className={headerTextClassName}
          />
        ),
        cell: (info: CellContext<IHubCatalogType, unknown>) => {
          const parentIds = info.row.original.parentCatalogTypes ?? [];
          if (!parentIds.length) return "";
          const labels = parentIds
            .map(
              (idOrName) =>
                idOrNameToLabelMap.get(String(idOrName)) ?? String(idOrName),
            )
            .filter(Boolean);
          return (
            <div className="flex flex-wrap gap-1.5">
              {labels.map((label) => (
                <Badge
                  key={label}
                  variant="secondary"
                  appearance="default"
                  size="sm">
                  {label}
                </Badge>
              ))}
            </div>
          );
        },
      },
      {
        id: "action",
        header: t("hub.catalog_manager.reviews_column_actions"),
        enableSorting: false,
        cell: (info: CellContext<IHubCatalogType, unknown>) =>
          hasCatalogTypeManage ? (
            <ToolbarGroup className="gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  const typeId = info.row.original.typeId;
                  if (typeId != null) {
                    navigate(
                      `/${Paths.CatalogTypeManager}/${Paths.EditCatalogType}?typeId=${typeId}`,
                    );
                  }
                }}
                aria-label={t("hub.administer.edit")}>
                <Pencil className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(info.row.original);
                }}
                aria-label={t("hub.catalog_manager.delete")}>
                <Trash2 className="size-4 text-destructive" />
              </Button>
            </ToolbarGroup>
          ) : null,
      },
    ],
    [
      hasCatalogTypeManage,
      t,
      headerTextClassName,
      navigate,
      idOrNameToLabelMap,
    ],
  );

  return (
    <WithPermission permissions={[HUB_PERM.CATALOG_TYPE_MANAGE]}>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <DataGrid<IHubCatalogType>
          data={catalogTypes}
          columns={columns}
          recordCount={totalCount}
          tableLayout={DATA_GRID_LAYOUT}
          onRowClick={handleRowClick}
          withExport
          withSearch={{
            searchKey: "label",
            placeholder: t("hub.catalog_type_manager.search_placeholder"),
            onChange: setSearchQuery,
          }}
          afterActions={afterActions}
          paginationPlacement="toolbar"
          paginationConfig={{ showRowsPerPage: true }}
          manualPagination
          pageCount={Math.ceil(totalCount / pagination.pageSize)}
          pagination={pagination}
          onPaginationChange={setPagination}
          loading={isLoading}
          loadingMessage={t("hub.administer.loading")}
          emptyMessage={t("hub.catalog_type_manager.no_data_available")}
        />

        <DetailsPanel
          open={!!selectedCatalogType}
          onOpenChange={(open) => !open && handleClosePanel()}
          title={t("hub.catalog_type_manager.details")}
          items={detailsPanelItems}
          onEdit={hasCatalogTypeManage ? detailsPanelOnEdit : undefined}
          onDelete={
            selectedCatalogType && hasCatalogTypeManage
              ? () => {
                  setDeleteTarget(selectedCatalogType);
                  handleClosePanel();
                }
              : undefined
          }
          deleteLabel={t("hub.catalog_manager.delete")}
          cancelLabel={t("hub.administer.cancel")}
          editLabel={t("hub.administer.edit")}
          footer={
            isFormLoading || formData?.formConfig ? (
              <Card className="mt-4">
                <CardHeader>
                  <CardTitle>
                    {t("hub.catalog_type_manager.form_preview", {
                      defaultValue: "Form Preview",
                    })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {formData?.formConfig && (
                    <FormRenderer
                      generatedJson={formData.formConfig}
                      isFieldsDisabled
                      isLoading={isFormLoading}
                      showSubmitButton={false}
                      showResetButton={false}
                    />
                  )}
                </CardContent>
              </Card>
            ) : null
          }
        />

        <ConfirmAction
          open={!!deleteTarget}
          onOpenChange={(open) => !open && setDeleteTarget(null)}
          title={t("hub.catalog_type_manager.delete_title")}
          description={t("hub.catalog_type_manager.delete_description", {
            name:
              deleteTarget?.label ?? deleteTarget?.name ?? "this catalog type",
          })}
          confirmText={t("hub.catalog_manager.delete")}
          cancelText={t("hub.administer.cancel")}
          actionVariant="destructive"
          onConfirm={handleDeleteConfirm}
        />
      </div>
    </WithPermission>
  );
}
